import DropdownContainer from 'components/Luxkit/Dropdown/DropdownContainer'
import ListItem from 'components/Luxkit/List/ListItem'
import ListItemPrice from 'components/Luxkit/List/ListItemPrice'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import { OfferPageActions, takeOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { pluralizeToString } from 'lib/pluralize'
import React, { useCallback, useContext, useMemo } from 'react'
import { getDurationOptions } from 'selectors/multipleRoomBookingSelectors'
import styled from 'styled-components'
import { OfferDurationOption } from '../../DurationTabs/OfferDurationTabs'
import { min } from 'lib/arrayUtils'
import RadioButton from 'components/Luxkit/RadioButton/RadioButton'

const SizedDropdownContainer = styled(DropdownContainer)`
  max-height: 60vh;
`

function getOptionLabel(option: OfferDurationOption) {
  if (option.soldOut) {
    return 'SOLD OUT'
  } else if (option.lowestPrice === undefined) {
    return undefined
  } else {
    return 'From'
  }
}

interface Props {
  durationOptions: Array<OfferDurationOption>;
  selectedDuration: number;
  onClose?: () => void;
  bundledWithFlightsOnly: boolean;
  currentOfferId?: string;
  bundleOfferId?: string;
  offerId?: string;
}

function OfferCalendarDurationDropdown(props: Props) {
  const { durationOptions, onClose, selectedDuration, bundledWithFlightsOnly, currentOfferId, bundleOfferId } = props
  const dispatch = useOfferPageDispatch()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const onDurationClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const duration = parseInt(e.currentTarget.dataset.duration!, 10)
    dispatch({ type: OfferPageActions.setDuration, duration, offerId: currentOfferId })
    onClose?.()
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseSubFilter, CALENDAR_GA_EVENT_LABEL.OpenDuration)
  }, [dispatch, onClose, onUserAnalyticsAction, currentOfferId])
  const minDuration = useMemo(() => min(durationOptions, duration => duration.duration)!, [durationOptions])

  return (
    <SizedDropdownContainer
      title="How long do you want to stay?"
      subtitle={`Minimum stay for this offer is ${pluralizeToString('night', minDuration.duration)}`}
      onClose={onClose}
    >
      {durationOptions.map(option => <ListItem
        key={option.duration}
        title={pluralizeToString('night', option.duration)}
        selected={option.duration === selectedDuration}
        data-duration={option.duration}
        onClick={onDurationClick}
        startIcon={<RadioButton name="nights" checked={option.duration === selectedDuration} />}
      >
        <ListItemPrice
          price={option.soldOut || bundledWithFlightsOnly || bundleOfferId ? undefined : option.lowestPrice}
          label={bundledWithFlightsOnly || bundleOfferId ? undefined : getOptionLabel(option)}
        />
      </ListItem>)}
    </SizedDropdownContainer>
  )
}

export default withOfferPageAndReduxState((state, appState, props) => {
  const currentOfferId = props.offerId ?? state.paginator?.currentOfferId
  const currentState = takeOfferState(state, currentOfferId)

  return {
    selectedDuration: currentState.duration,
    bundleOfferId: currentState.bundleOffer?.id,
    currentOfferId,
    durationOptions: getDurationOptions(currentState, appState),
    bundledWithFlightsOnly: currentState.offer?.bundledWithFlightsOnly,
  }
})(OfferCalendarDurationDropdown)
